import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Building2, ClipboardList, Loader2, Upload, X } from "lucide-react";

// Reusable components
import { PropertySelector } from "@/components/forms/property-selector";
import { UnitSelector } from "@/components/forms/unit-selector";
import { SectionHeader } from "@/components/ui/section-header";
import { TenantStatusIndicator } from "@/components/maintenance/tenant-status-indicator";

import { DialogFooter } from "@/components/ui/dialog";
import { AppDialog } from "@/components/ui/app-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import {
  addMaintenanceRequest,
  CreateMaintenanceRequestData,
} from "@/lib/maintenance-service";
import { uploadMaintenanceImages } from "@/lib/storage-service";
import {
  Property,
  MaintenanceCategory,
  MaintenancePriority,
  MaintenanceStatus,
} from "@/types/types";

// Form schema
const maintenanceFormSchema = z.object({
  property: z.string().min(1, "Please select a property"),
  unitId: z.string().optional(),
  category: z.string().min(1, "Please select a category"),
  title: z
    .string()
    .min(5, "Title must be at least 5 characters")
    .max(100, "Title must be less than 100 characters"),
  description: z
    .string()
    .min(10, "Description must be at least 10 characters")
    .max(500, "Description must be less than 500 characters"),
  priority: z.string().min(1, "Please select a priority level"),
  status: z.string().optional(),
  images: z.array(z.string()).optional(),
});

type MaintenanceFormData = z.infer<typeof maintenanceFormSchema>;

interface TenantLease {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantEmail: string;
  propertyId: string;
  unitId?: string;
  unitName?: string;
}

interface MaintenanceRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  properties: Property[];
  tenantLeases?: TenantLease[];
  userId: string;
  // For tenant use: pre-fill property and unit
  prefilledData?: {
    propertyId?: string;
    unitId?: string;
    tenantId?: string;
  };
  // Mode: landlord can create on behalf of tenant, tenant creates for themselves
  mode?: "landlord" | "tenant";
}

export function MaintenanceRequestDialog({
  open,
  onOpenChange,
  onSuccess,
  properties,
  tenantLeases = [],
  userId,
  prefilledData,
  mode = "landlord",
}: MaintenanceRequestDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("video/")
    );

    if (validFiles.length !== files.length) {
      toast.error(
        "Some files were skipped. Only images and videos are allowed."
      );
    }

    setImageFiles((prev) => [...prev, ...validFiles]);

    // Create preview URLs
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrls((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const form = useForm<MaintenanceFormData>({
    resolver: zodResolver(maintenanceFormSchema),
    defaultValues: {
      property: "",
      unitId: "",
      category: "",
      title: "",
      description: "",
      priority: "medium",
      status: mode === "tenant" ? "pending" : "pending",
    },
  });

  // Auto-fill property and unit when dialog opens with prefilled data
  useEffect(() => {
    if (open && prefilledData?.propertyId) {
      // Set property first
      form.setValue("property", prefilledData.propertyId);
      // Then set unit if provided
      if (prefilledData.unitId) {
        // Use setTimeout to ensure property is set first
        setTimeout(() => {
          form.setValue("unitId", prefilledData.unitId);
        }, 0);
      }
    } else if (!open) {
      // Reset form when dialog closes
      form.reset();
    }
  }, [open, prefilledData?.propertyId, prefilledData?.unitId]);

  // Watch property selection to filter tenants/units
  const selectedPropertyId = form.watch("property");
  const selectedUnitId = form.watch("unitId");

  // Find active tenant for selected property/unit
  const getActiveTenant = (): TenantLease | undefined => {
    if (!selectedPropertyId) return undefined;

    if (selectedUnitId) {
      // Multi-unit: Check for tenant in specific unit
      return tenantLeases.find(
        (tl) =>
          tl.propertyId === selectedPropertyId && tl.unitId === selectedUnitId
      );
    } else {
      // Single-unit: Check for tenant in property
      return tenantLeases.find(
        (tl) => tl.propertyId === selectedPropertyId && !tl.unitId
      );
    }
  };

  const activeTenant = mode === "landlord" ? getActiveTenant() : undefined;

  const handleSubmit = async (data: MaintenanceFormData) => {
    setIsSubmitting(true);
    try {
      // Find selected property
      const property = properties.find((p) => p.id === data.property);

      if (!property) {
        toast.error("Property not found");
        return;
      }

      let unitName = "Main Unit";
      let unitId = "";
      let tenantId = "";
      let tenantName = "";
      let tenantEmail = "";

      if (mode === "tenant") {
        // Tenant mode: use prefilled data
        unitId = data.unitId || "";
        tenantId = prefilledData?.tenantId || userId;
        tenantName = ""; // Will be populated from user doc
        tenantEmail = ""; // Will be populated from user doc

        if (unitId && property.units) {
          const unit = property.units.find((u) => u.id === unitId);
          unitName = unit?.name || unitId;
        }
      } else {
        // Landlord mode: find tenant from lease
        if (data.unitId) {
          unitId = data.unitId;
          if (property.units) {
            const unit = property.units.find((u) => u.id === data.unitId);
            unitName = unit?.name || data.unitId;
          }
        }

        tenantId = activeTenant?.tenantId || "";
        tenantName = activeTenant?.tenantName || "";
        tenantEmail = activeTenant?.tenantEmail || "";
      }

      // Upload images if any
      let imageUrls: string[] = [];
      if (imageFiles.length > 0) {
        try {
          // Create a temporary ID for the request
          const tempRequestId = `temp_${Date.now()}`;
          imageUrls = await uploadMaintenanceImages(
            userId,
            tempRequestId,
            imageFiles
          );
        } catch (error) {
          console.error("Error uploading images:", error);
          toast.error(
            "Failed to upload images. Request will be created without images."
          );
        }
      }

      const requestData: CreateMaintenanceRequestData = {
        propertyId: data.property,
        propertyTitle: property.title,
        propertyAddress: property.address,
        unitId,
        unitName,
        tenantId,
        tenantName,
        tenantEmail,
        tenantPhone: "",
        category: data.category as MaintenanceCategory,
        description: data.description,
        priority: data.priority as MaintenancePriority,
        status: (data.status as MaintenanceStatus) || "pending",
        title: data.title,
        submittedBy: userId,
        ...(imageUrls.length > 0 && { images: imageUrls }), // Only include if not empty
      };

      await addMaintenanceRequest(requestData);

      toast.success("Maintenance request created successfully");
      form.reset();
      setImageFiles([]);
      setPreviewUrls([]);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Error creating maintenance request:", error);
      toast.error("Failed to create maintenance request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedProperty = properties.find((p) => p.id === selectedPropertyId);
  const isMultiUnit = selectedProperty?.structure === "multi-unit";

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="New Maintenance Request"
      description={
        mode === "landlord"
          ? "Report an issue at one of your properties. If there's an active tenant for the selected property/unit, they will be automatically notified."
          : "Report an issue with your rental property. Your landlord will be notified and will address the issue as soon as possible."
      }
      maxWidth="3xl"
      footer={
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} form="maintenance-form">
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? "Creating..." : "Create Request"}
          </Button>
        </DialogFooter>
      }
    >
      <Form {...form}>
        <form
          id="maintenance-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="space-y-8"
        >
          {/* Property & Location Section */}
          <div className="space-y-6">
            <SectionHeader icon={Building2} title="Property & Location" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <PropertySelector
                control={form.control}
                properties={properties}
                disabled={!!prefilledData?.propertyId}
                onPropertyChange={() => form.setValue("unitId", "")}
                required
              />

              {/* Conditional Unit Dropdown for Multi-Unit Properties */}
              {isMultiUnit && (
                <UnitSelector
                  control={form.control}
                  units={selectedProperty?.units || []}
                  disabled={!!prefilledData?.unitId}
                  required={false}
                  showStatus
                />
              )}
            </div>

            {/* Tenant Status Indicator (Landlord mode only) */}
            {mode === "landlord" && selectedPropertyId && (
              <TenantStatusIndicator
                tenant={
                  activeTenant
                    ? {
                        tenantName: activeTenant.tenantName,
                        tenantEmail: activeTenant.tenantEmail,
                      }
                    : undefined
                }
              />
            )}
          </div>

          {/* Issue Details Section */}
          <div className="space-y-6 pt-6 border-t">
            <SectionHeader icon={ClipboardList} title="Issue Details" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base">Category *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="w-full h-11">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="plumbing">🚰 Plumbing</SelectItem>
                        <SelectItem value="electrical">
                          ⚡ Electrical
                        </SelectItem>
                        <SelectItem value="hvac">❄️ HVAC</SelectItem>
                        <SelectItem value="appliance">🔧 Appliance</SelectItem>
                        <SelectItem value="structural">
                          🏗️ Structural
                        </SelectItem>
                        <SelectItem value="pest">🐛 Pest Control</SelectItem>
                        <SelectItem value="landscaping">
                          🌳 Landscaping
                        </SelectItem>
                        <SelectItem value="security">🔒 Security</SelectItem>
                        <SelectItem value="other">📋 Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-base">Priority *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      defaultValue="medium"
                    >
                      <FormControl>
                        <SelectTrigger className="w-full h-11">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="high">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-red-500" />
                            <span>High - Urgent</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="medium">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-orange-500" />
                            <span>Medium - Normal</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="low">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-2 rounded-full bg-green-500" />
                            <span>Low - Can wait</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base">Issue Title *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Leaking kitchen sink"
                      className="h-11"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base">Description *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe the issue in detail..."
                      className="min-h-[120px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Image/Video Upload */}
            <div className="space-y-3">
              <FormLabel className="text-base">Attach Photos/Videos</FormLabel>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                    id="image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      document.getElementById("image-upload")?.click()
                    }
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Images or Videos
                  </Button>
                </div>

                {previewUrls.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {previewUrls.map((url, index) => (
                      <div
                        key={index}
                        className="relative group aspect-square rounded-lg overflow-hidden border"
                      >
                        {imageFiles[index]?.type.startsWith("video/") ? (
                          <video
                            src={url}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <img
                            src={url}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 p-1 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Upload photos or videos to help explain the issue (Max 10MB
                  per file)
                </p>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </AppDialog>
  );
}
